import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getSpecification, getAllSpecifications, createSpecification, updateSpecification, deleteSpecification, type Specification } from '../api/specification'
import { getDeviceContacts, getTerminalInternalConnections, createDeviceContact, createTerminalInternalConnection, updateDeviceContact, deleteDeviceContact } from '../api/deviceContacts'
import { createDeviceInstance } from '../api/deviceInstances'
import { getElectricalPanels } from '../api/electricalPanels'
// import { getSchemaTypes } from '../api/schemaTypes' // Legacy, не используется
import { getSchemas } from '../api/schemas'
import { getDeviceTypesList } from '../api/deviceTypesList'
import { getCableTypes } from '../api/cableTypes'
import type { DeviceContact } from '../types/connections'
import { useToast } from '../hooks/useToast'

interface ProjectSpecificationProps {
  projectId: number
}

// Device types will be loaded dynamically from API

const CONTACT_COLORS = [
  { value: '', label: 'Не указан', color: 'transparent' },
  { value: 'WH', label: 'WH - Белый', color: '#FFFFFF' },
  { value: 'BK', label: 'BK - Чёрный', color: '#000000' },
  { value: 'BU', label: 'BU - Синий', color: '#0066CC' },
  { value: 'BN', label: 'BN - Коричневый', color: '#8B4513' },
  { value: 'GY', label: 'GY - Серый', color: '#808080' },
  { value: 'GN', label: 'GN - Зеленый', color: '#008000' },
  { value: 'GNYE', label: 'GNYE - Жёлто-зелёный', color: '#FFD700', color2: '#008000' },
  { value: 'YE', label: 'YE - Желтый', color: '#FFD700' },
  { value: 'RD', label: 'RD - Красный', color: '#FF0000' },
  { value: 'OG', label: 'OG - Оранжевый', color: '#FFA500' },
  { value: 'PK', label: 'PK - Розовый', color: '#FFC0CB' },
  { value: 'VT', label: 'VT - Фиолетовый', color: '#8A2BE2' },
  // Цвета витой пары
  { value: 'WO', label: 'WO - Бело-оранжевый', color: '#FFFFFF', color2: '#FFA500' },
  { value: 'WG', label: 'WG - Бело-зелёный', color: '#FFFFFF', color2: '#008000' },
  { value: 'WB', label: 'WB - Бело-синий', color: '#FFFFFF', color2: '#0066CC' },
  { value: 'WN', label: 'WN - Бело-коричневый', color: '#FFFFFF', color2: '#8B4513' }
]

// Функция для получения иконки по типу схемы (legacy, для обратной совместимости)
// const getSchemaTypeIcon = (typeCode: string): string => {
//   switch (typeCode) {
//     case 'main': return '📊'
//     case 'breaker': return '⚡'
//     case 'knx': return '🔌'
//     case 'rs485': return '🔗'
//     case 'ethernet': return '🌐'
//     case 'terminal': return '🔧'
//     default: return '📋'
//   }
// }

const CONTACT_POSITIONS = [
  { value: 'top', label: 'Верхний', icon: '⬆️' },
  { value: 'bottom', label: 'Нижний', icon: '⬇️' },
  { value: 'left', label: 'Левый', icon: '⬅️' },
  { value: 'right', label: 'Правый', icon: '➡️' }
]






export default function ProjectSpecification({ projectId }: ProjectSpecificationProps) {
  const toast = useToast()
  const [selectedDevice, setSelectedDevice] = useState<Specification | null>(null)
  const [contacts, setContacts] = useState<DeviceContact[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showContactManager, setShowContactManager] = useState(false)
  const [showContactEditor, setShowContactEditor] = useState(false)
  const [editingContact, setEditingContact] = useState<DeviceContact | null>(null)
  const [newContact, setNewContact] = useState<{
    contact_number: number | string
    contact_label: string
    color_code: string
    contact_position: 'top' | 'bottom' | 'left' | 'right'
    selected_schema_ids: number[] // Массив ID конкретных схем щита
  }>({
    contact_number: 1,
    contact_label: '',
    color_code: '',
    contact_position: 'top',
    selected_schema_ids: [] // По умолчанию пусто
  })

  const [copyDevice, setCopyDevice] = useState<Specification | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)
  const [currentDocumentationUrl, setCurrentDocumentationUrl] = useState<string>('')
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('')

  // Terminal-specific modals
  const [showTerminalCreateForm, setShowTerminalCreateForm] = useState(false)

  // Cable creation modal
  const [showCableCreateForm, setShowCableCreateForm] = useState(false)

  // Device instance creation modal
  const [showCreateInstanceForm, setShowCreateInstanceForm] = useState(false)
  const [selectedDeviceForInstance, setSelectedDeviceForInstance] = useState<Specification | null>(null)
  const [selectedElectricalPanelId, setSelectedElectricalPanelId] = useState<number | null>(null)

  // Device type manager state

  // Copy from existing devices modal
  const [showCopyFromExistingModal, setShowCopyFromExistingModal] = useState(false)
  const [copyFilterType, setCopyFilterType] = useState<string>('')

  // Cable wires editor state
  const [newWire, setNewWire] = useState<{ wire_number: number; color_code: string }>({ wire_number: 1, color_code: 'WH' })


  const [filterType, setFilterType] = useState<string>('')

  // Sorting state
  const [sortField, setSortField] = useState<keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count'>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const queryClient = useQueryClient()

  // Initialize URL when edit modal opens
  useEffect(() => {
    if (selectedDevice) {
      setCurrentDocumentationUrl(selectedDevice.documentation_url || '');
      setSelectedDeviceType(selectedDevice.device_type_code || '');
    }
  }, [selectedDevice]);

  // Load device contacts when device is selected
  useEffect(() => {
    if (selectedDevice) {
      loadDeviceContacts(selectedDevice.id)
    }
  }, [selectedDevice])

  const loadDeviceContacts = async (specificationId: number) => {
    try {
      const deviceContacts = await getDeviceContacts(specificationId)
      // Сортируем контакты по номеру (числовая сортировка)
      const sortedContacts = deviceContacts.sort((a, b) => {
        const numA = parseInt(String(a.contact_number))
        const numB = parseInt(String(b.contact_number))
        return numA - numB
      })
      setContacts(sortedContacts)
    } catch (error) {
      console.error('Failed to load device contacts:', error)
      setContacts([])
    }
  }

  const { data: devices = [], isLoading: devicesLoading, error } = useQuery({
    queryKey: ['specification', projectId, filterType],
    queryFn: () => getSpecification(projectId, filterType || undefined),
    enabled: !!projectId,
  })

  // Function to close all modals
  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setShowContactManager(false)
    setShowContactEditor(false)
    setShowTerminalCreateForm(false)
    setShowCableCreateForm(false)
    setShowCreateInstanceForm(false)
    setShowCopyFromExistingModal(false)
    setSelectedDevice(null)
    setSelectedDeviceForInstance(null)
    setSelectedElectricalPanelId(null)
    setCopyDevice(null)
    setMouseDownTarget(null)
  }

  // Load electrical panels for instance creation
  const { data: electricalPanels = [], isLoading: electricalPanelsLoading } = useQuery({
    queryKey: ['electrical-panels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: !!projectId && showCreateInstanceForm
  })

  // Load schema types for contact type selection (legacy, для обратной совместимости)
  // const { data: schemaTypes = [], isLoading: schemaTypesLoading } = useQuery({
  //   queryKey: ['schema-types'],
  //   queryFn: getSchemaTypes
  // })

  // Load all panels for the project
  const { data: allPanels = [] } = useQuery({
    queryKey: ['electrical-panels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: !!projectId
  })

  // Load schemas for all panels in the project
  const { data: allSchemas = [], isLoading: panelSchemasLoading } = useQuery({
    queryKey: ['schemas-all-panels', projectId],
    queryFn: async () => {
      if (!projectId || allPanels.length === 0) return []
      
      // Загружаем схемы для всех щитов проекта
      const schemasPromises = allPanels.map(panel => 
        getSchemas(projectId, panel.id).catch(err => {
          console.error(`Failed to load schemas for panel ${panel.id}:`, err)
          return []
        })
      )
      
      const schemasArrays = await Promise.all(schemasPromises)
      // Объединяем все схемы в один массив
      return schemasArrays.flat()
    },
    enabled: !!projectId && allPanels.length > 0
  })

  // Load device types for dropdown
  const { data: deviceTypes = [], isLoading: deviceTypesLoading } = useQuery({
    queryKey: ['device-types-list'],
    queryFn: getDeviceTypesList
  })

  const { data: cableTypes = [] } = useQuery({
    queryKey: ['cableTypes'],
    queryFn: getCableTypes
  })

  // Handle ESC key to close modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAllModals()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Sort devices
  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]
      
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1
      
      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }
      
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [devices, sortField, sortDirection])

  // Event handlers
  const handleEditClick = (device: Specification) => {
    setSelectedDevice(device)
    setShowEditForm(true)
  }

  const handleContactsClick = async (device: Specification) => {
    setSelectedDevice(device)
    try {
      const deviceContacts = await getDeviceContacts(device.id)
      setContacts(deviceContacts)
      setShowContactManager(true)
    } catch (error) {
      console.error('Error loading contacts:', error)
      setContacts([])
      setShowContactManager(true)
    }
  }

  const handleCopyClick = (device: Specification) => {
    setCopyDevice(device)
    if (device.device_type_code === 'terminal') {
      setShowTerminalCreateForm(true)
    } else {
      setShowCreateForm(true)
    }
  }

  const handleDocumentationClick = (device: Specification) => {
    if (device.documentation_url) {
      setCurrentDocumentationUrl(device.documentation_url)
      window.open(device.documentation_url, '_blank')
    }
  }

  const handleDeleteClick = (device: Specification) => {
    if (confirm(`Вы уверены, что хотите удалить устройство "${device.name}"?`)) {
      deleteSpecificationMutation.mutate(device.id)
    }
  }

  const handleCreateDeviceInstance = (device: Specification) => {
    setSelectedDeviceForInstance(device)
    setShowCreateInstanceForm(true)
  }

  // Handle form submissions
  const handleCreateDevice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const deviceTypeCode = formData.get('device_type') as string || 'lighting'
    
    const deviceData: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> = {
      project_id: projectId,
      device_type: deviceTypeCode, // Send device_type code instead of device_type_id
      name: formData.get('name') as string,
      description: formData.get('description') as string || undefined,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined,
      length_meters: parseFloat(formData.get('length_meters') as string) || undefined,
      width: parseFloat(formData.get('width') as string) || undefined,
      height: parseFloat(formData.get('height') as string) || undefined,
      depth: parseFloat(formData.get('depth') as string) || undefined,
      visual_template: formData.get('visual_template') as string || undefined,
      documentation_url: formData.get('documentation_url') as string || undefined
    }



    createSpecificationMutation.mutate(deviceData)
  }

  const handleCreateCable = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const wireCount = parseInt(String(formData.get('wire_count') || '0'))
    const section = parseFloat(String(formData.get('section') || '0'))
    const cableType = String(formData.get('cable_type') || '').trim()
    const autoName = cableType && wireCount && section ? `${cableType} ${wireCount}×${section}` : ''

    const cableData: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> = {
      project_id: projectId,
      device_type_id: 7, // ID для кабелей в device_types
      name: autoName,
      model: autoName,
      manufacturer: (formData.get('manufacturer') as string) || 'Standard',
      length_meters: parseFloat(String(formData.get('length_meters') || '0')) || 100,
      properties: {
        wire_count: wireCount,
        section: section,
        cable_type: cableType,
        voltage_rating: '0.66kV',
        fire_safety: 'нг',
        flexibility: (formData.get('flexibility') as string) || 'rigid'
      }
    }

    createSpecificationMutation.mutate(cableData)
  }

  const handleEditDevice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedDevice) return
    
    const formData = new FormData(e.currentTarget)
    
            if (selectedDevice.device_type_code === 'cable') {
      const wireCount = parseInt(String(formData.get('wire_count') || '0'))
      const section = parseFloat(String(formData.get('section') || '0'))
      const cableType = String(formData.get('cable_type') || '').trim()
      const autoName = cableType && wireCount && section ? `${cableType} ${wireCount}×${section}` : (selectedDevice.name || '')

      const updates = {
        name: autoName,
        model: autoName,
        manufacturer: (formData.get('manufacturer') as string) || 'Standard',
        length_meters: parseFloat(String(formData.get('length_meters') || '0')) || 100,
        device_type_id: selectedDevice.device_type_id || 7, // Ensure device_type_id is always set for cables
        properties: {
          wire_count: wireCount,
          section: section,
          cable_type: cableType,
          voltage_rating: '0.66kV',
          fire_safety: 'нг',
          flexibility: (formData.get('flexibility') as string) || 'rigid'
        }
      }

      updateSpecificationMutation.mutate({ id: selectedDevice.id, data: updates })
      return
    }

    // Map device type codes to IDs (since API doesn't return IDs)
    const deviceTypeIdMap: Record<string, number> = {
      'lighting': 1,
      'outlet': 2, 
      'switch': 3,
      'sensor': 4,
      'actuator': 5,
      'breaker': 6,
      'cable': 7,
      'terminal': 8,
      'other': 9,
      'breaker1': 10,
      'breaker2': 11,
      'Executive': 12,
      'Electricdrives': 13,
      'knxdev': 14
    };

    const updates: Partial<Specification> = {
      name: formData.get('name') as string,
      description: formData.get('description') as string || undefined,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(String(formData.get('power_consumption') || '')) || undefined,
      length_meters: parseFloat(String(formData.get('length_meters') || '')) || undefined,
      width: parseFloat(String(formData.get('width') || '')) || undefined,
      height: parseFloat(String(formData.get('height') || '')) || undefined,
      depth: parseFloat(String(formData.get('depth') || '')) || undefined,
      visual_template: formData.get('visual_template') as string || undefined,
      documentation_url: currentDocumentationUrl || undefined,
      device_type_id: deviceTypeIdMap[selectedDeviceType] || selectedDevice.device_type_id
    }

    updateSpecificationMutation.mutate({ id: selectedDevice.id, data: updates })
  }

  const handleCreateTerminal = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const terminalData: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> = {
      project_id: projectId,
      device_type_id: 8, // ID для клеммников в device_types
      name: formData.get('name') as string,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined
    }

    createSpecificationMutation.mutate(terminalData)
  }

  const handleSubmitDeviceInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!selectedDeviceForInstance) {
      alert('Ошибка: не выбрано устройство')
      return
    }

    if (!selectedElectricalPanelId) {
      alert('Пожалуйста, выберите электрический щит')
      return
    }
    
    const formData = new FormData(e.currentTarget)
    const positionLabel = formData.get('position_label') as string
    const description = formData.get('description') as string
    const knxAddress = formData.get('knx_address') as string
    
    if (!positionLabel.trim()) {
      alert('Пожалуйста, введите позиционное обозначение')
      return
    }
    
    const instanceData = {
      specification_id: selectedDeviceForInstance.id,
      project_id: projectId,
      electrical_panel_id: selectedElectricalPanelId,
      position_label: positionLabel.trim(),
      description: description.trim() || undefined,
      knx_address: knxAddress?.trim() || undefined
    }

    createDeviceInstanceMutation.mutate(instanceData)
  }

  // Contact functions
  const closeContactEditor = () => {
    setShowContactEditor(false)
    setEditingContact(null)
    setNewContact({
      contact_number: 1,
      contact_label: '',
      color_code: '',
      contact_position: 'top',
      selected_schema_ids: []
    })
    setNewWire({ wire_number: 1, color_code: 'WH' })
  }

  // Функция для получения следующего номера контакта
  const getNextContactNumber = () => {
    if (contacts.length === 0) return 1
    
    // Извлекаем номера контактов
    const contactNumbers = contacts
      .map(c => parseInt(c.contact_number.toString()))
      .filter(num => !isNaN(num))
    
    return contactNumbers.length > 0 ? Math.max(...contactNumbers) + 1 : 1
  }

  const handleOpenNewContactEditor = () => {
    // Reset editing state and set calculated contact number
    setEditingContact(null)
    setNewContact({
      contact_number: getNextContactNumber(),
      contact_label: '',
      color_code: '',
      contact_position: 'top',
      selected_schema_ids: [] // Начинаем с пустого массива
    })
    setShowContactEditor(true)
  }

  const handleEditContact = (contact: DeviceContact) => {
    setEditingContact(contact)
    
    // Convert schema_ids (или schema_type_ids для обратной совместимости) в selected_schema_ids
    let selected_schema_ids: number[] = [];
    
    // Сначала проверяем новое поле schema_ids
    if ((contact as any).schema_ids) {
      try {
        if (Array.isArray((contact as any).schema_ids)) {
          selected_schema_ids = (contact as any).schema_ids;
        } else if (typeof (contact as any).schema_ids === 'string') {
          selected_schema_ids = JSON.parse((contact as any).schema_ids);
        }
      } catch (error) {
        console.warn('Error parsing schema_ids:', error);
      }
    }
    
    // Fallback: если schema_ids нет, пытаемся получить схемы по schema_type_ids
    if (selected_schema_ids.length === 0 && contact.schema_type_ids && allSchemas.length > 0) {
      try {
        let schemaTypeIds: number[] = [];
        if (Array.isArray(contact.schema_type_ids)) {
          schemaTypeIds = contact.schema_type_ids;
        } else if (typeof contact.schema_type_ids === 'string') {
          schemaTypeIds = JSON.parse(contact.schema_type_ids);
        }
        
        // Находим все схемы с подходящими типами
        selected_schema_ids = allSchemas
          .filter(schema => schemaTypeIds.includes(schema.schema_type_id || 0))
          .map(schema => schema.id);
      } catch (error) {
        console.warn('Error parsing schema_type_ids:', error);
      }
    }
    
    setNewContact({
      contact_number: contact.contact_number,
      contact_label: contact.contact_label || '',
      color_code: contact.color_code || '',
      contact_position: contact.contact_position || 'top',
      selected_schema_ids: selected_schema_ids
    })
    setShowContactEditor(true)
  }

  const handleDeleteContact = (contact: DeviceContact) => {
    if (confirm(`Удалить контакт "${contact.contact_label || contact.contact_number}"?`)) {
      deleteContactMutation.mutate(contact.id)
    }
  }

  const handleCreateContact = () => {
    if (!selectedDevice) return

    // Отправляем schema_ids (массив ID конкретных схем)
    const schema_ids = newContact.selected_schema_ids || [];

    const contactData = {
      specification_id: selectedDevice.id,
      contact_number: String(newContact.contact_number!),
      contact_label: newContact.contact_label || '',
      color_code: newContact.color_code || '',
      contact_position: newContact.contact_position || 'top',
      schema_ids: schema_ids // Новое поле для конкретных схем
    }

    createContactMutation.mutate(contactData)
  }

  const handleUpdateContact = () => {
    if (!editingContact) return

    // Отправляем schema_ids (массив ID конкретных схем)
    const schema_ids = newContact.selected_schema_ids || [];

    const updates = {
      contact_number: String(newContact.contact_number!),
      contact_label: newContact.contact_label || '',
      color_code: newContact.color_code || '',
      contact_position: newContact.contact_position || 'top',
      schema_ids: schema_ids
    }

    updateContactMutation.mutate({ id: editingContact.id, updates })
  }

  // Cable wire CRUD based on paired contacts (TOP/BOTTOM)
  const handleSaveWire = async () => {
    if (!selectedDevice) return
    const wireNumber = newWire.wire_number
    const color = newWire.color_code
    try {
      const wireList: any[] = getWiresFromContacts()
      const existing = wireList.find(w => w.wire_number === wireNumber)
      if (!existing) {
        // create top/bottom pair
        await createDeviceContact({
          specification_id: selectedDevice.id,
          contact_number: `${wireNumber}-TOP`,
          contact_label: '',
          color_code: color,
          contact_position: 'top'
        })
        await createDeviceContact({
          specification_id: selectedDevice.id,
          contact_number: `${wireNumber}-BOTTOM`,
          contact_label: '',
          color_code: color,
          contact_position: 'bottom'
        })
      } else {
        // update both
        if (existing.topContact) {
          await updateDeviceContact(existing.topContact.id, { color_code: color })
        }
        if (existing.bottomContact) {
          await updateDeviceContact(existing.bottomContact.id, { color_code: color })
        }
      }
      await loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      toast.success(existing ? 'Жила успешно обновлена' : 'Жила успешно создана')
    } catch (err) {
      console.error('Wire save failed:', err)
      toast.error('Ошибка при сохранении жилы')
    }
  }

  const handleDeleteWire = async (wire: any) => {
    if (!selectedDevice) return
    if (!confirm(`Удалить жилу №${wire.wire_number}?`)) return
    try {
      if (wire.topContact) await deleteDeviceContact(wire.topContact.id)
      if (wire.bottomContact) await deleteDeviceContact(wire.bottomContact.id)
      await loadDeviceContacts(selectedDevice.id)
    } catch (err) {
      console.error('Wire delete failed:', err)
      toast.error('Ошибка при удалении жилы')
    }
  }

  const handleQuickAddContactSet = async (items: { label: string, color: string }[]) => {
    if (!selectedDevice) return
    
    // Используем выбранные схемы из формы
    const schema_ids = newContact.selected_schema_ids || [];

    try {
      let next = getNextContactNumber()
      
      for (const item of items) {
        await createDeviceContact({
          specification_id: selectedDevice.id,
          contact_number: String(next),
          contact_label: item.label,
          color_code: item.color,
          contact_position: 'top',
          schema_ids: schema_ids
        })
        next += 1
      }
      
      await loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      toast.success(`Контакты успешно добавлены (${items.length} шт.)`)
    } catch (err) {
      console.error('Quick add contact-set failed:', err)
      toast.error('Ошибка при быстром добавлении контактов')
    }
  }

  const handleQuickAddWireSet = async (colors: string[]) => {
    if (!selectedDevice) return
    try {
      let next = getNextWireNumber()
      for (const color of colors) {
        await createDeviceContact({
          specification_id: selectedDevice.id,
          contact_number: `${next}-TOP`,
          contact_label: '',
          color_code: color,
          contact_position: 'top'
        })
        await createDeviceContact({
          specification_id: selectedDevice.id,
          contact_number: `${next}-BOTTOM`,
          contact_label: '',
          color_code: color,
          contact_position: 'bottom'
        })
        next += 1
      }
      await loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      toast.success(`Жилы успешно добавлены (${colors.length} шт.)`)
    } catch (err) {
      console.error('Quick add wire-set failed:', err)
      toast.error('Ошибка при быстром добавлении жил')
    }
  }

  const handleQuickAddTwistedPair = async () => {
    // Стандарт T568B для витой пары
    const twistedPairColors = ['WO', 'OG', 'WG', 'BU', 'WB', 'GN', 'WN', 'BN']
    await handleQuickAddWireSet(twistedPairColors)
  }

  // Load all specifications for copying
  const { data: allSpecifications = [], isLoading: allSpecificationsLoading } = useQuery({
    queryKey: ['all-specifications', copyFilterType],
    queryFn: () => getAllSpecifications(copyFilterType || undefined),
    enabled: showCopyFromExistingModal,
  })

  // Function to copy specification from existing
  const copySpecificationFromExisting = async (sourceSpec: Specification) => {
    try {
      // 1. Create new specification
      const newSpecData: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> = {
        project_id: projectId,
        device_type_id: sourceSpec.device_type_id,
        name: `${sourceSpec.name} (копия)`,
        description: sourceSpec.description,
        model: sourceSpec.model,
        manufacturer: sourceSpec.manufacturer,
        unit_price: sourceSpec.unit_price,
        power_consumption: sourceSpec.power_consumption,
        length_meters: sourceSpec.length_meters,
        width: sourceSpec.width,
        height: sourceSpec.height,
        depth: sourceSpec.depth,
        visual_template: sourceSpec.visual_template,
        documentation_url: sourceSpec.documentation_url,
        properties: sourceSpec.properties,
      }

      const newSpec = await createSpecification(newSpecData)

      // 2. Copy device contacts
      const sourceContacts = await getDeviceContacts(sourceSpec.id)
      const contactIdMap = new Map<number, number>() // Map old contact IDs to new contact IDs

      for (const contact of sourceContacts) {
        const newContact = await createDeviceContact({
          specification_id: newSpec.id,
          contact_number: contact.contact_number,
          contact_position: contact.contact_position,
          contact_label: contact.contact_label,
          color_code: contact.color_code,
          properties: contact.properties,
          schema_type_ids: contact.schema_type_ids,
        })
        contactIdMap.set(contact.id, newContact.id)
      }

      // 3. For terminals: copy internal connections
      if (sourceSpec.device_type_code === 'terminal') {
        const internalConnections = await getTerminalInternalConnections(sourceSpec.id)
        for (const connection of internalConnections) {
          const newFromContactId = contactIdMap.get(connection.contact_from_id)
          const newToContactId = contactIdMap.get(connection.contact_to_id)
          
          if (newFromContactId && newToContactId) {
            await createTerminalInternalConnection({
              specification_id: newSpec.id,
              contact_from_id: newFromContactId,
              contact_to_id: newToContactId,
              level_number: connection.level_number,
              connection_type: connection.connection_type,
              description: connection.description,
            })
          }
        }
      }

      // 4. For cables: regular copy (nuances to be worked out later)
      // Currently handled the same as regular devices

      queryClient.invalidateQueries({ queryKey: ['specification', projectId] })
      setShowCopyFromExistingModal(false)
      toast.success(`Спецификация "${sourceSpec.name}" успешно скопирована`)
    } catch (error) {
      console.error('Error copying specification:', error)
      toast.error('Ошибка при копировании спецификации')
    }
  }

  // Mutations
  const createSpecificationMutation = useMutation({
    mutationFn: createSpecification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', projectId] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
      setShowCreateForm(false)
      setShowTerminalCreateForm(false)
      setSelectedDevice(null)
      setCopyDevice(null)
      toast.success('Спецификация успешно создана')
    },
    onError: (error) => {
      console.error('Error creating specification:', error)
      toast.error('Ошибка при создании спецификации')
    }
  })

  // Cable wire helpers
  const getNextWireNumber = () => {
    if (contacts.length === 0) return 1
    const wireNumbers = contacts
      .map(c => parseInt(c.contact_number.toString().split('-')[0]))
      .filter(n => !isNaN(n))
    return wireNumbers.length > 0 ? Math.max(...wireNumbers) + 1 : 1
  }

  const getWiresFromContacts = () => {
    const map = new Map<number, any>()
    contacts.forEach(contact => {
      const numStr = contact.contact_number.toString()
      const n = parseInt(numStr.split('-')[0])
      if (isNaN(n)) return
      if (!map.has(n)) {
        map.set(n, { wire_number: n, color_code: contact.color_code || 'WH', topContact: null, bottomContact: null })
      }
      const w = map.get(n)
      if (numStr.includes('-TOP')) w.topContact = contact
      if (numStr.includes('-BOTTOM')) w.bottomContact = contact
    })
    return Array.from(map.values()).sort((a, b) => a.wire_number - b.wire_number)
  }

  const updateSpecificationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Specification> }) => 
      updateSpecification(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', projectId] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
      setShowEditForm(false)
      setShowTerminalCreateForm(false)
      setSelectedDevice(null)
      toast.success('Спецификация успешно обновлена')
    },
    onError: (error) => {
      console.error('Error updating specification:', error)
      alert('Ошибка при обновлении спецификации')
    }
  })

  const deleteSpecificationMutation = useMutation({
    mutationFn: deleteSpecification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', projectId] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
      toast.success('Спецификация успешно удалена')
    },
    onError: (error) => {
      console.error('Error deleting specification:', error)
      toast.error('Ошибка при удалении спецификации')
    }
  })

  const createDeviceInstanceMutation = useMutation({
    mutationFn: createDeviceInstance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', projectId] })
      setShowCreateInstanceForm(false)
      setSelectedDeviceForInstance(null)
      toast.success('Экземпляр устройства успешно создан')
    },
    onError: (error) => {
      console.error('Error creating device instance:', error)
      toast.error('Ошибка при создании экземпляра устройства')
    }
  })

  // Contact mutations
  const createContactMutation = useMutation({
    mutationFn: createDeviceContact,
    onSuccess: () => {
      if (selectedDevice) {
        loadDeviceContacts(selectedDevice.id)
      }
      closeContactEditor()
      toast.success('Контакт успешно создан')
    },
    onError: (error) => {
      console.error('Error creating contact:', error)
      toast.error('Ошибка при создании контакта')
    }
  })

  const updateContactMutation = useMutation({
    mutationFn: async (data: { id: number; updates: any }) => {
      await updateDeviceContact(data.id, data.updates)
    },
    onSuccess: () => {
      if (selectedDevice) {
        loadDeviceContacts(selectedDevice.id)
      }
      closeContactEditor()
      toast.success('Контакт успешно обновлен')
    },
    onError: (error) => {
      console.error('Error updating contact:', error)
      toast.error('Ошибка при обновлении контакта')
    }
  })

  const deleteContactMutation = useMutation({
    mutationFn: deleteDeviceContact,
    onSuccess: () => {
      if (selectedDevice) {
        loadDeviceContacts(selectedDevice.id)
      }
      toast.success('Контакт успешно удален')
    },
    onError: (error) => {
      console.error('Error deleting contact:', error)
      toast.error('Ошибка при удалении контакта')
    }
  })

  const handleSort = (field: keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortableHeader = ({ field, children }: { field: keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count'; children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          <span className="text-blue-600">
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </div>
    </th>
  )

  if (devicesLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Загрузка спецификаций устройств...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Ошибка загрузки спецификаций: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Devices Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🔧 Спецификации устройств</h2>
          <div className="flex flex-col gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-auto"
              disabled={deviceTypesLoading}
            >
              <option value="">Все типы</option>
              {deviceTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm whitespace-nowrap"
              >
                ➕ Добавить устройство
              </button>
              <button
                onClick={() => setShowTerminalCreateForm(true)}
                className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                🔗 Добавить клеммник
              </button>
              <button
                onClick={() => setShowCableCreateForm(true)}
                className="bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 text-sm whitespace-nowrap"
              >
                🔌 Добавить кабель
              </button>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {devicesLoading && (
          <div className="text-center py-8 text-gray-500">Загрузка спецификаций устройств...</div>
        )}

        {/* Devices table - Desktop */}
        {!devicesLoading && devices.length > 0 && (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader field="id">ID</SortableHeader>
                    <SortableHeader field="manufacturer">Производитель</SortableHeader>
                    <SortableHeader field="model">Модель</SortableHeader>
                    <SortableHeader field="name">Название</SortableHeader>
                    <SortableHeader field="instance_count">Количество экземпляров</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px] sticky right-0 bg-gray-50 z-10">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedDevices.map((device: Specification) => (
                    <tr 
                      key={device.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[80px]">
                        #{device.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[150px]">
                        {device.manufacturer || 'Не указан'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[150px]">
                        {device.model || 'Не указана'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap min-w-[300px]">
                        <div className="text-sm font-medium text-gray-900">
                          <Link
                            to={`/projects/${projectId}/devices/${device.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {device.name}
                          </Link>
                        </div>
                        <div className="text-sm text-gray-500">
                          {deviceTypes.find(t => t.value === device.device_type_code)?.label || device.device_type_name || 'Неизвестный тип'}
                          {device.power_consumption && ` • ${device.power_consumption}Вт`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[120px]">
                        <Link
                          to={device.device_type_code === 'cable'
                            ? `/projects/${projectId}/cable-instances/spec?spec=${device.id}`
                            : `/projects/${projectId}/device-instances?spec=${device.id}`
                          }
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {device.instance_count || 0}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium w-[200px] sticky right-0 bg-white z-10 border-l border-gray-200">
                        <div className="flex flex-col items-end space-y-1">
                          {/* Первая строка: 3 иконки на всех экранах */}
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleEditClick(device)}
                              className="action-icon-btn edit"
                              title="Редактировать"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleContactsClick(device)}
                              className="action-icon-btn contacts"
                              title="Управление контактами"
                            >
                              ⚡
                            </button>
                            <button
                              onClick={() => handleCopyClick(device)}
                              className="action-icon-btn copy"
                              title="Копировать"
                            >
                              📋
                            </button>
                          </div>
                          {/* Вторая строка: 4 иконки на всех экранах */}
                          <div className="flex items-center space-x-1">
                            {device.documentation_url ? (
                              <button
                                onClick={() => handleDocumentationClick(device)}
                                className="action-icon-btn documentation"
                                title="Документация"
                              >
                                📖
                              </button>
                            ) : (
                              <div className="w-8 h-8 flex items-center justify-center">
                                {/* Placeholder to maintain alignment */}
                              </div>
                            )}
                            <button
                              onClick={() => handleDeleteClick(device)}
                              className="action-icon-btn delete"
                              title="Удалить"
                            >
                              🗑️
                            </button>
                            <button
                              onClick={() => handleCreateDeviceInstance(device)}
                              className="action-icon-btn add-instance"
                              title="Создать экземпляр"
                            >
                              ➕
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {sortedDevices.map((device: Specification) => (
                <div
                  key={device.id}
                  className="border rounded-lg p-4 border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        <Link
                          to={`/projects/${projectId}/devices/${device.id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {device.name}
                        </Link> <span className="text-gray-400">#{device.id}</span>
                      </h3>
                      <div className="text-xs text-gray-600 mt-1">
                        <div>{device.manufacturer || 'Производитель не указан'}</div>
                        <div>{device.model || 'Модель не указана'}</div>
                        <div>{deviceTypes.find(t => t.value === device.device_type_code)?.label || device.device_type_name || 'Неизвестный тип'}</div>
                        <div className="font-medium">
                          Количество экземпляров:{' '}
                          <Link
                            to={device.device_type_code === 'cable'
                              ? `/projects/${projectId}/cable-instances/spec?spec=${device.id}`
                              : `/projects/${projectId}/device-instances?spec=${device.id}`
                            }
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {device.instance_count || 0}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-1 ml-2">
                      {/* Первая строка: 3 иконки */}
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditClick(device)}
                          className="action-icon-btn edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleContactsClick(device)}
                          className="action-icon-btn contacts"
                          title="Управление контактами"
                        >
                          ⚡
                        </button>
                        <button
                          onClick={() => handleCopyClick(device)}
                          className="action-icon-btn copy"
                          title="Копировать"
                        >
                          📋
                        </button>
                      </div>
                      {/* Вторая строка: 4 иконки */}
                      <div className="flex space-x-1">
                        {device.documentation_url ? (
                          <button
                            onClick={() => handleDocumentationClick(device)}
                            className="action-icon-btn documentation"
                            title="Документация"
                          >
                            📖
                          </button>
                        ) : (
                          <div className="w-8 h-8 flex items-center justify-center">
                            {/* Placeholder to maintain alignment */}
                          </div>
                        )}
                        <button
                          onClick={() => handleDeleteClick(device)}
                          className="action-icon-btn delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                        <button
                          onClick={() => handleCreateDeviceInstance(device)}
                          className="action-icon-btn add-instance"
                          title="Создать экземпляр"
                        >
                          ➕
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty state */}
        {!devicesLoading && devices.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">Спецификации устройств не найдены</p>
            <p>Добавьте первое устройство или клеммник для начала работы</p>
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">
              {copyDevice ? `Копировать "${copyDevice.name}"` : 'Создать спецификацию устройства'}
            </h3>
            <form onSubmit={handleCreateDevice} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Тип устройства</label>
                  <select
                    name="device_type"
                    required
                    defaultValue={copyDevice?.device_type_code || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={deviceTypesLoading}
                  >
                    <option value="">Выберите тип...</option>
                    {deviceTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Название</label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={copyDevice ? `${copyDevice.name} (копия)` : ''}
                    placeholder="напр., QF 16A, Розетка 220В"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Описание</label>
                  <textarea
                    name="description"
                    defaultValue={copyDevice?.description || ''}
                    placeholder="Описание спецификации устройства"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Модель</label>
                  <input
                    type="text"
                    name="model"
                    defaultValue={copyDevice?.model || ''}
                    placeholder="напр., C16 2P"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Производитель</label>
                  <input
                    type="text"
                    name="manufacturer"
                    defaultValue={copyDevice?.manufacturer || ''}
                    placeholder="напр., ABB, Schneider"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Потребляемая мощность (Вт)</label>
                  <input
                    type="number"
                    name="power_consumption"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.power_consumption || ''}
                    placeholder="напр., 15.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Длина (метры)</label>
                  <input
                    type="number"
                    name="length_meters"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.length_meters || ''}
                    placeholder="Только для кабелей"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Ширина (мм)</label>
                  <input
                    type="number"
                    name="width"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.width || ''}
                    placeholder="напр., 90"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Высота (мм)</label>
                  <input
                    type="number"
                    name="height"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.height || ''}
                    placeholder="напр., 100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Глубина (мм)</label>
                  <input
                    type="number"
                    name="depth"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.depth || ''}
                    placeholder="напр., 65"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Визуальный шаблон</label>
                <input
                  type="text"
                  name="visual_template"
                  placeholder="SVG или идентификатор шаблона"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL документации</label>
                <input
                  type="url"
                  name="documentation_url"
                  defaultValue={copyDevice?.documentation_url || ''}
                  placeholder="https://example.com/docs/device"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {copyDevice?.documentation_url && (
                  <div className="mt-2">
                    <a
                      href={copyDevice.documentation_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      📖 Открыть документацию
                      <span className="text-xs">(откроется в новом окне)</span>
                    </a>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false)
                    setShowCopyFromExistingModal(true)
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  📋 Скопировать из существующих в БД
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={createSpecificationMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {createSpecificationMutation.isPending ? 'Создание...' : 'Создать устройство'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedDevice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedDevice.device_type_code === 'cable' ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Редактировать кабель</h3>
                  <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <form onSubmit={handleEditDevice} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Производитель</label>
                      <input name="manufacturer" type="text" defaultValue={selectedDevice.manufacturer || 'Standard'} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Тип кабеля *</label>
                      <select name="cable_type" required className="w-full px-3 py-2 border border-gray-300 rounded-md" defaultValue={(selectedDevice as any).properties?.cable_type || ''}>
                        <option value="">Выберите тип...</option>
                        {cableTypes.filter(ct => ct.is_active).map(type => (
                          <option key={type.id} value={type.name}>{type.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Количество жил *</label>
                      <input name="wire_count" type="number" required min="1" max="50" defaultValue={(selectedDevice as any).properties?.wire_count || 3} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Сечение (мм²) *</label>
                      <input name="section" type="number" step="0.01" required min="0.1" defaultValue={(selectedDevice as any).properties?.section || 1.5} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Длина (м)</label>
                      <input name="length_meters" type="number" step="0.1" min="0" defaultValue={selectedDevice.length_meters || 100} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Гибкость</label>
                      <select name="flexibility" className="w-full px-3 py-2 border border-gray-300 rounded-md" defaultValue={(selectedDevice as any).properties?.flexibility || 'rigid'}>
                        <option value="rigid">Жесткий</option>
                        <option value="flexible">Гибкий</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={closeAllModals} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
                    <button type="submit" disabled={updateSpecificationMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{updateSpecificationMutation.isPending ? 'Сохранение...' : 'Сохранить'}</button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">Изменить спецификацию устройства</h3>
                <form onSubmit={handleEditDevice} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Тип устройства</label>
                      <select 
                        name="device_type" 
                        value={selectedDeviceType}
                        onChange={(e) => setSelectedDeviceType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {deviceTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Название</label>
                      <input type="text" name="name" required defaultValue={selectedDevice.name} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Описание</label>
                      <textarea
                        name="description"
                        defaultValue={selectedDevice.description || ''}
                        placeholder="Описание спецификации устройства"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Модель</label>
                      <input type="text" name="model" defaultValue={selectedDevice.model || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Производитель</label>
                      <input type="text" name="manufacturer" defaultValue={selectedDevice.manufacturer || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Потребляемая мощность (Вт)</label>
                      <input type="number" name="power_consumption" step="0.1" min="0" defaultValue={selectedDevice.power_consumption || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Длина (метры)</label>
                      <input type="number" name="length_meters" step="0.1" min="0" defaultValue={selectedDevice.length_meters || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Ширина (мм)</label>
                      <input
                        type="number"
                        name="width"
                        step="0.1"
                        min="0"
                        defaultValue={selectedDevice.width || ''}
                        placeholder="напр., 90"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Высота (мм)</label>
                      <input
                        type="number"
                        name="height"
                        step="0.1"
                        min="0"
                        defaultValue={selectedDevice.height || ''}
                        placeholder="напр., 100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Глубина (мм)</label>
                      <input
                        type="number"
                        name="depth"
                        step="0.1"
                        min="0"
                        defaultValue={selectedDevice.depth || ''}
                        placeholder="напр., 65"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Визуальный шаблон</label>
                    <input type="text" name="visual_template" defaultValue={selectedDevice.visual_template || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">URL документации</label>
                    <input type="url" name="documentation_url" value={currentDocumentationUrl} onChange={(e) => setCurrentDocumentationUrl(e.target.value)} placeholder="https://example.com/docs/device" className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    {currentDocumentationUrl && (
                      <div className="mt-2">
                        <a href={currentDocumentationUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                          📖 Открыть документацию
                          <span className="text-xs">(откроется в новом окне)</span>
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button type="button" onClick={closeAllModals} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
                    <button type="submit" disabled={updateSpecificationMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{updateSpecificationMutation.isPending ? 'Обновление...' : 'Обновить устройство'}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact Management Modal */}
      {showContactManager && selectedDevice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedDevice.device_type_code === 'cable' ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">🔌 Жилы кабеля "{selectedDevice.name}"</h3>
                  <div className="flex items-center space-x-2">
                    <button onClick={() => { setNewWire({ wire_number: getNextWireNumber(), color_code: 'WH' }); setShowContactEditor(true) }} className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">➕ Добавить жилу</button>
                    <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-600">✕</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {getWiresFromContacts().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-3">Жилы не созданы</p>
                      <button onClick={() => { setNewWire({ wire_number: 1, color_code: 'WH' }); setShowContactEditor(true) }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">Создать первую жилу</button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Цвет</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Визуально</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getWiresFromContacts().map((wire: any) => (
                            <tr key={wire.wire_number} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{wire.wire_number}</td>
                              <td className="px-4 py-3 whitespace-nowrap"><span className="text-xs bg-gray-100 px-2 py-1 rounded">{CONTACT_COLORS.find(c => c.value === wire.color_code)?.label || 'Неизвестный'}</span></td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {(() => {
                                  const colorInfo = CONTACT_COLORS.find(c => c.value === wire.color_code);
                                  const color = colorInfo?.color || '#ccc';
                                  const color2 = colorInfo?.color2;
                                  return (
                                    <div 
                                      className="w-6 h-4 rounded border border-gray-400"
                                      style={{
                                        backgroundColor: color2 ? 'transparent' : color,
                                        backgroundImage: color2
                                          ? `linear-gradient(135deg, ${color} 0%, ${color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color2} 53%, ${color2} 100%)`
                                          : 'none',
                                        backgroundSize: color2 ? '100% 100%' : 'auto',
                                        backgroundPosition: 'center'
                                      }}
                                    />
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <div className="flex justify-end space-x-1">
                                  <button onClick={() => { setEditingContact(wire.topContact || wire.bottomContact); setNewWire({ wire_number: wire.wire_number, color_code: wire.color_code }); setShowContactEditor(true) }} className="action-icon-btn edit" title="Редактировать">✏️</button>
                                  <button onClick={() => handleDeleteWire(wire)} className="action-icon-btn delete" title="Удалить">🗑️</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end">
                  <button onClick={closeAllModals} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Закрыть</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">⚡ Контакты устройства "{selectedDevice.name}"</h3>
                  <div className="flex items-center space-x-2">
                    <button onClick={handleOpenNewContactEditor} className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">➕ Добавить контакт</button>
                    <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-600">✕</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {contacts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-3">Контакты не созданы</p>
                      <button onClick={handleOpenNewContactEditor} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">Создать первый контакт</button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Маркировка</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Место</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Цвет</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Показать</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {contacts.map((contact) => (
                            <tr key={contact.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm font-medium text-blue-600">#{contact.contact_number}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{contact.contact_label || `Контакт ${contact.contact_number}`}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{contact.contact_position ? CONTACT_POSITIONS.find(p => p.value === contact.contact_position)?.label || contact.contact_position : 'Не указано'}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{contact.color_code ? CONTACT_COLORS.find(c => c.value === contact.color_code)?.label || contact.color_code : 'Не указан'}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center"><div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: contact.color_code ? (CONTACT_COLORS.find(c => c.value === contact.color_code)?.color || '#ccc') : 'transparent' }} /></div></td>
                              <td className="px-4 py-3 whitespace-nowrap text-right"><div className="flex justify-end space-x-1"><button onClick={() => handleEditContact(contact)} className="action-icon-btn edit" title="Редактировать">✏️</button><button onClick={() => handleDeleteContact(contact)} className="action-icon-btn delete" title="Удалить">🗑️</button></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end"><button onClick={closeAllModals} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Закрыть</button></div>
              </>
            )}
          </div>
        </div>
      )}

            {/* Contact Editor Modal */}
      {showContactEditor && selectedDevice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeContactEditor()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedDevice.device_type_code === 'cable' ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{editingContact ? 'Редактировать жилу' : 'Новая жила'}</h3>
                  <button onClick={closeContactEditor} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="space-y-4">
                  {!editingContact && (
                    <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Быстрое добавление наборов жил</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <button onClick={() => handleQuickAddWireSet(['WH','BU','GNYE'])} className="px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">WH, BU, GNYE</button>
                        <button onClick={() => handleQuickAddWireSet(['WH','BK','BU','GNYE'])} className="px-2 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">WH, BK, BU, GNYE</button>
                        <button onClick={() => handleQuickAddWireSet(['WH','BU'])} className="px-2 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700">WH, BU</button>
                        <button onClick={handleQuickAddTwistedPair} className="px-2 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700">витуха</button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Номер жилы</label>
                    <input type="number" min="1" value={newWire.wire_number} onChange={(e)=> setNewWire({ ...newWire, wire_number: parseInt(e.target.value)||1 })} className="w-full px-3 py-2 border border-gray-300 rounded-md" disabled={!!editingContact} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Цвет жилы *</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {CONTACT_COLORS.filter(c=>c.value!=='').map(color => (
                        <button key={color.value} type="button" onClick={()=> setNewWire({ ...newWire, color_code: color.value })} className={`flex items-center space-x-2 p-2 rounded-md border transition-colors ${newWire.color_code===color.value?'border-blue-500 bg-blue-50':'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                          <div 
                            className="w-4 h-4 rounded border border-gray-400 flex-shrink-0 relative overflow-hidden"
                            style={{
                              backgroundColor: color.color2 ? 'transparent' : color.color,
                              backgroundImage: color.color2
                                ? `linear-gradient(135deg, ${color.color} 0%, ${color.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color.color2} 53%, ${color.color2} 100%)`
                                : 'none',
                              backgroundSize: color.color2 ? '100% 100%' : 'auto',
                              backgroundPosition: 'center'
                            }}
                          />
                          <span className="text-xs text-gray-700">{color.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 text-sm text-gray-600">Выбрано: <span className="font-medium">{CONTACT_COLORS.find(c=>c.value===newWire.color_code)?.label || 'Не указан'}</span></div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button onClick={closeContactEditor} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
                    <button onClick={handleSaveWire} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Сохранить</button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{editingContact ? 'Редактировать контакт' : 'Новый контакт'}</h3>
                  <button onClick={closeContactEditor} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="space-y-4">
                  {!editingContact && (
                    <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Быстрое добавление групп контактов</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => handleQuickAddContactSet([{label:'L', color:'BN'}, {label:'N', color:'BU'}, {label:'PE', color:'GNYE'}])} className="px-2 py-1.5 bg-yellow-600 text-white rounded text-xs font-medium hover:bg-yellow-700">L, N, PE</button>
                        <button onClick={() => handleQuickAddContactSet([{label:'L', color:'BN'}, {label:'N', color:'BU'}])} className="px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">L, N</button>
                        <button onClick={() => handleQuickAddContactSet([{label:'+24V', color:'RD'}, {label:'GND', color:'BK'}])} className="px-2 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700">+24V, GND</button>
                        <button onClick={() => handleQuickAddContactSet([{label:'A', color:'WH'}, {label:'B', color:'BU'}, {label:'GND', color:'BK'}])} className="px-2 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">RS-485 (A, B, G)</button>
                        <button onClick={() => handleQuickAddContactSet([{label:'+', color:'RD'}, {label:'-', color:'BK'}])} className="px-2 py-1.5 bg-red-800 text-white rounded text-xs font-medium hover:bg-red-900">KNX (+, -)</button>
                        <button onClick={() => handleQuickAddContactSet([{label:'1', color:'GY'}, {label:'2', color:'GY'}])} className="px-2 py-1.5 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700">Сухой (1, 2)</button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Номер контакта</label>
                    <input type="text" value={newContact.contact_number} onChange={(e)=> setNewContact(prev=>({ ...prev, contact_number: e.target.value }))} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); handleCreateContact() } }} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="1 или 1-TOP" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Маркировка</label>
                    <input type="text" value={newContact.contact_label} onChange={(e)=> setNewContact(prev=>({ ...prev, contact_label: e.target.value }))} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); handleCreateContact() } }} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Li, Lo, N, PE" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">На каких схемах отображать</label>
                    {panelSchemasLoading ? (
                      <div className="text-sm text-gray-500 mt-2">Загрузка схем...</div>
                    ) : allSchemas.length === 0 ? (
                      <div className="text-sm text-gray-500 mt-2">У проекта нет схем</div>
                    ) : (
                      <div className="mt-2 space-y-4">
                        {allPanels.map(panel => {
                          // Фильтруем схемы для этого щита
                          const panelSchemas = allSchemas.filter(schema => schema.electrical_panel_id === panel.id)
                          
                          // Пропускаем щиты без схем
                          if (panelSchemas.length === 0) return null
                          
                          return (
                            <div key={panel.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="text-sm font-semibold text-gray-700 mb-2">
                                Щит {panel.name}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {panelSchemas.map(schema => (
                                  <button
                                    key={schema.id}
                                    type="button"
                                    onClick={() => {
                                      setNewContact(prev => {
                                        const isSelected = prev.selected_schema_ids.includes(schema.id);
                                        const updatedIds = isSelected
                                          ? prev.selected_schema_ids.filter(id => id !== schema.id)
                                          : [...prev.selected_schema_ids, schema.id];
                                        
                                        return {
                                          ...prev,
                                          selected_schema_ids: updatedIds
                                        };
                                      });
                                    }}
                                    className={`flex items-center space-x-2 px-3 py-2 rounded-md border transition-colors ${
                                      newContact.selected_schema_ids.includes(schema.id)
                                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                                    }`}
                                  >
                                    <span className="text-sm">📋</span>
                                    <span className="text-sm font-medium">{schema.name}</span>
                                    {newContact.selected_schema_ids.includes(schema.id) && (
                                      <span className="text-xs text-blue-600">✓</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {newContact.selected_schema_ids.length > 0 && (
                      <div className="mt-3 text-sm text-gray-600">
                        Выбрано схем: {newContact.selected_schema_ids.length}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Расположение контакта</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {CONTACT_POSITIONS.map(position => (
                        <button key={position.value} type="button" onClick={()=> setNewContact(prev=>({ ...prev, contact_position: position.value as 'top'|'bottom'|'left'|'right' }))} className={`flex items-center space-x-2 px-3 py-2 rounded-md border transition-colors ${newContact.contact_position===position.value?'border-blue-500 bg-blue-50 text-blue-700':'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                          <span className="text-sm">{position.icon}</span>
                          <span className="text-sm font-medium">{position.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Цвет (необязательно)</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {CONTACT_COLORS.map(color => (
                        <button key={color.value} type="button" onClick={()=> setNewContact(prev=>({ ...prev, color_code: color.value }))} className={`flex items-center space-x-2 p-2 rounded-md border transition-colors ${newContact.color_code===color.value?'border-blue-500 bg-blue-50':'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                          <div 
                            className="w-4 h-4 rounded border border-gray-400 flex-shrink-0 relative overflow-hidden"
                            style={{
                              backgroundColor: color.color2 ? 'transparent' : color.color,
                              backgroundImage: color.color2
                                ? `linear-gradient(135deg, ${color.color} 0%, ${color.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color.color2} 53%, ${color.color2} 100%)`
                                : 'none',
                              backgroundSize: color.color2 ? '100% 100%' : 'auto',
                              backgroundPosition: 'center'
                            }}
                          />
                          <span className="text-xs text-gray-700">{color.value ? color.label : 'Нет'}</span>
                        </button>
                      ))}
                    </div>
                    {newContact.color_code && (<div className="mt-2 text-sm text-gray-600">Выбран: <span className="font-medium">{CONTACT_COLORS.find(c=>c.value===newContact.color_code)?.label || 'Не указан'}</span></div>)}
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button onClick={closeContactEditor} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
                    <button onClick={editingContact ? handleUpdateContact : handleCreateContact} disabled={createContactMutation.isPending || updateContactMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{editingContact ? (updateContactMutation.isPending ? 'Сохранение...' : 'Сохранить') : (createContactMutation.isPending ? 'Создание...' : 'Создать')}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Terminal Create Form Modal */}
      {showTerminalCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">🔗 Создать клеммный терминал</h3>
            <form onSubmit={handleCreateTerminal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Количество уровней *</label>
                <input
                  type="number"
                  name="contact_count"
                  min="1"
                  max="10"
                  defaultValue="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Например: 3 для 3-уровневого клеммника"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Указывайте количество уровней. Система автоматически создаст контакты (1U, 1D, 2U, 2D, и т.д.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Название клеммника *</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Например: ЗНИ-2.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Модель</label>
                <input
                  type="text"
                  name="model"
                  placeholder="Например: ЗНИ-2.5мм²"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Производитель</label>
                <input
                  type="text"
                  name="manufacturer"
                  placeholder="Например: ЭКФ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Максимальный ток (А)</label>
                <input
                  type="number"
                  name="power_consumption"
                  step="0.1"
                  placeholder="Например: 24"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createSpecificationMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createSpecificationMutation.isPending ? 'Создание...' : 'Создать клеммник'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Device Instance Creation Modal */}
      {showCreateInstanceForm && selectedDeviceForInstance && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Создать экземпляр устройства</h3>
            <form onSubmit={handleSubmitDeviceInstance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Спецификация</label>
                <input
                  type="text"
                  value={selectedDeviceForInstance.name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Электрический щит *</label>
                {electricalPanelsLoading ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600">
                    Загрузка щитов...
                  </div>
                ) : electricalPanels.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600">
                    Нет доступных щитов
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {electricalPanels.map(panel => (
                      <button
                        key={panel.id}
                        type="button"
                        onClick={() => setSelectedElectricalPanelId(panel.id)}
                        className={`px-4 py-2 rounded-md border transition-colors text-sm font-medium ${
                          selectedElectricalPanelId === panel.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {panel.name} ({panel.proj_id})
                      </button>
                    ))}
                  </div>
                )}
                {selectedElectricalPanelId && (
                  <div className="mt-2 text-sm text-green-600">
                    ✅ Выбран щит: {electricalPanels.find(p => p.id === selectedElectricalPanelId)?.name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Позиционное обозначение *</label>
                <input
                  type="text"
                  name="position_label"
                  required
                  placeholder="Например: ДП-14.2-1, LD-1-1, УР-2-4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание (необязательно)</label>
                <textarea
                  name="description"
                  rows={3}
                  placeholder="Дополнительное описание экземпляра..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* KNX Address field - show for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
              {selectedDeviceForInstance && (selectedDeviceForInstance.device_type_id === 14 || selectedDeviceForInstance.device_type_id === 5) && (
                <div>
                  <label className="block text-sm font-medium mb-1">KNX адрес</label>
                  <input
                    type="text"
                    name="knx_address"
                    placeholder="напр., 1.1.1"
                    pattern="^\d+\.\d+\.\d+$"
                    title="Формат: 1.255.255"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Формат: 1.255.255</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createDeviceInstanceMutation.isPending || !selectedElectricalPanelId}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {createDeviceInstanceMutation.isPending ? 'Создание...' : 'Создать экземпляр'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cable Creation Modal */}
      {showCableCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Новый кабель
              </h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCable} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Производитель</label>
                  <input
                    name="manufacturer"
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Завод Электрокабель"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Тип кабеля *</label>
                  <select name="cable_type" required className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="">Выберите тип...</option>
                    {cableTypes.filter(ct => ct.is_active).map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Количество жил *</label>
                  <input
                    name="wire_count"
                    type="number"
                    required
                    min="1"
                    max="50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Сечение (мм²) *</label>
                  <input
                    name="section"
                    type="number"
                    step="0.01"
                    required
                    min="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="1.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Длина (м)</label>
                  <input
                    name="length_meters"
                    type="number"
                    step="0.1"
                    min="0"
                    defaultValue="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Гибкость</label>
                  <select name="flexibility" className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="rigid">Жесткий</option>
                    <option value="flexible">Гибкий</option>
                    <option value="extra_flexible">Сверхгибкий</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createSpecificationMutation.isPending}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {createSpecificationMutation.isPending ? 'Создание...' : 'Создать кабель'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Copy from existing devices modal */}
      {showCopyFromExistingModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              setShowCopyFromExistingModal(false)
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Скопировать из существующих в БД</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Фильтр по типу устройства</label>
              <select
                value={copyFilterType}
                onChange={(e) => setCopyFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Все типы</option>
                {deviceTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {allSpecificationsLoading ? (
              <div className="text-center py-8 text-gray-500">Загрузка устройств...</div>
            ) : allSpecifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Устройства не найдены</div>
            ) : (
              <div className="grid gap-3 max-h-[60vh] overflow-y-auto">
                {allSpecifications.map((spec) => (
                  <div
                    key={spec.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => copySpecificationFromExisting(spec)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            spec.device_type_color ? `bg-[${spec.device_type_color}]` : 'bg-gray-100'
                          }`}>
                            {spec.device_type_icon} {spec.device_type_name || spec.device_type_code}
                          </span>
                          <h4 className="font-semibold text-gray-900">{spec.name}</h4>
                        </div>
                        {spec.model && (
                          <p className="text-sm text-gray-600 mb-1">Модель: {spec.model}</p>
                        )}
                        {spec.manufacturer && (
                          <p className="text-sm text-gray-600 mb-1">Производитель: {spec.manufacturer}</p>
                        )}
                        {spec.description && (
                          <p className="text-sm text-gray-500 mt-1">{spec.description}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          copySpecificationFromExisting(spec)
                        }}
                        className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm whitespace-nowrap"
                      >
                        Копировать
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowCopyFromExistingModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

